import { useState } from 'react';

export const useRowManager = () => {
  const [rows, setRows] = useState([{}]); // Start with one empty row

  const handleAddRow = () => {
    // Get certification service from previous row if it exists
    const previousRow = rows[rows.length - 1];
    const previousCertService = previousRow?.certification_service_id;
    
    // Create new row with previous cert service if available
    const newRow = previousCertService ? { certification_service_id: previousCertService } : {};
    setRows([...rows, newRow]);
  };

  const handleRemoveRow = (index) => {
    const newRows = [...rows];
    newRows.splice(index, 1);
    setRows(newRows);
  };

  const handleCellChange = (rowIndex, field, value) => {
    // Skip update if value hasn't changed
    if (rows[rowIndex]?.[field] === value) return;

    const newRows = [...rows];
    newRows[rowIndex] = {
      ...newRows[rowIndex],
      [field]: value,
      // When certification service changes
      ...(field === 'certification_service_id' && {
        certification_service_id: !isNaN(value) ? parseInt(value) : value,
        // Clear certification number when service is cleared
        ...(value === '' && { certification_number: '' })
      }),
      // When CAC sticker changes
      ...(field === 'cac_sticker' && {
        cac_sticker: value || 'none'
      })
    };
    setRows(newRows);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, rowIndex, fieldIndex, fields = []) => {
    // Handle Tab at end of row, Enter key, and ArrowDown on last row
    if ((e.key === 'Tab' && !e.shiftKey && fields.length && fieldIndex === fields.length - 1) ||
        e.key === 'Enter' ||
        (e.key === 'ArrowDown' && rowIndex === rows.length - 1)) {
      e.preventDefault();
      const nextRow = rowIndex + 1;
      if (nextRow === rows.length) {
        handleAddRow();
      }
      // Focus next row's same field after a short delay to allow row creation
      setTimeout(() => {
        const nextInput = document.querySelector(
          `[data-row="${nextRow}"][data-field="${fields[fieldIndex].key}"]`
        );
        nextInput?.focus();
      }, 0);
    }
  };

  const validateRows = () => {
    const requiredFields = {
      denomination: 'Denomination',
      year: 'Year'
    };

    const validationErrors = rows.map(row => {
      if (!row || Object.keys(row).length === 0) return null;

      const missingFields = Object.entries(requiredFields)
        .filter(([key]) => !row[key])
        .map(([_, label]) => label);

      return missingFields.length > 0 ? missingFields : null;
    });

    return validationErrors.filter(error => error !== null);
  };

  const cleanRowData = (rowData) => {
    if (!rowData || Object.keys(rowData).length === 0) return null;

    // Clean up empty strings to null
    const cleanedData = { ...rowData };
    Object.keys(cleanedData).forEach(key => {
      if (cleanedData[key] === '') {
        cleanedData[key] = null;
      }
    });

    // Convert numeric fields
    if (cleanedData.composition) cleanedData.composition = parseFloat(cleanedData.composition);
    if (cleanedData.weight) cleanedData.weight = parseFloat(cleanedData.weight);
    if (cleanedData.diameter) cleanedData.diameter = parseFloat(cleanedData.diameter);
    if (cleanedData.mintage) cleanedData.mintage = parseInt(cleanedData.mintage);
    if (cleanedData.population) cleanedData.population = parseInt(cleanedData.population);
    if (cleanedData.pop_higher) cleanedData.pop_higher = parseInt(cleanedData.pop_higher);
    if (cleanedData.price_guide_value) cleanedData.price_guide_value = parseFloat(cleanedData.price_guide_value);
    if (cleanedData.list_price) cleanedData.list_price = parseFloat(cleanedData.list_price);

    // Handle CAC sticker enum
    if (!cleanedData.cac_sticker || !['green', 'gold', 'none'].includes(cleanedData.cac_sticker)) {
      cleanedData.cac_sticker = 'none';
    }

    return cleanedData;
  };

  return {
    rows,
    setRows,
    handleAddRow,
    handleRemoveRow,
    handleCellChange,
    handleKeyDown,
    validateRows,
    cleanRowData
  };
};
